import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Menu, ChevronDown, Globe, User, LogOut, LayoutDashboard } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_8c6a1162-6fec-4154-8fe3-0b918a141fc6/artifacts/ojlspj1s_Untitled%20design.png';

export const Header = () => {
  const { t, language, toggleLanguage, direction } = useLanguage();
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.about'), href: '/about' },
    {
      label: t('nav.services'),
      children: [
        { label: t('nav.recruitment'), href: '/services/recruitment' },
        { label: t('nav.training'), href: '/services/training' },
        { label: t('nav.consulting'), href: '/services/consulting' },
        { label: t('nav.careerAdvisory'), href: '/services/career-advisory' },
      ],
    },
    { label: t('nav.jobs'), href: '/jobs' },
    { label: t('nav.courses'), href: '/courses' },
    { label: t('nav.insights'), href: '/insights' },
    { label: t('nav.contact'), href: '/contact' },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <header className="glass-header fixed top-0 start-0 end-0 z-50" data-testid="main-header">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="WeDo HR Services" 
              className="h-10 md:h-12 w-auto" 
              style={{ mixBlendMode: 'multiply' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item, index) => (
              item.children ? (
                <DropdownMenu key={index}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="text-[#111827] hover:text-[#33AADD] hover:bg-transparent font-medium"
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.label}
                      <ChevronDown className={`ms-1 h-4 w-4 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align={direction === 'rtl' ? 'end' : 'start'} className="w-48">
                    {item.children.map((child, childIndex) => (
                      <DropdownMenuItem key={childIndex} asChild>
                        <Link
                          to={child.href}
                          className="w-full cursor-pointer"
                          data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {child.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={index}
                  to={item.href}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'text-[#33AADD]'
                      : 'text-[#111827] hover:text-[#33AADD]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </Link>
              )
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="hidden sm:flex items-center gap-1 text-[#6B7280] hover:text-[#33AADD]"
              data-testid="language-toggle"
            >
              <Globe className="h-4 w-4" />
              <span className="text-xs font-medium">{language === 'en' ? 'العربية' : 'EN'}</span>
            </Button>

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="hidden sm:flex items-center gap-2"
                    data-testid="user-menu-trigger"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-[#33AADD] flex items-center justify-center text-white text-sm font-medium">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm font-medium text-[#111827]">{user.name}</span>
                    <ChevronDown className="h-4 w-4 text-[#6B7280]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={direction === 'rtl' ? 'start' : 'end'} className="w-48">
                  {isAdmin && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="w-full cursor-pointer" data-testid="admin-dashboard-link">
                          <LayoutDashboard className="me-2 h-4 w-4" />
                          {t('nav.dashboard')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={logout} className="cursor-pointer" data-testid="logout-btn">
                    <LogOut className="me-2 h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="login-link">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="btn-primary" data-testid="register-link">
                    {t('nav.register')}
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side={direction === 'rtl' ? 'right' : 'left'} className="w-80">
                <div className="flex flex-col gap-6 mt-8">
                  {/* Mobile Language Toggle */}
                  <Button
                    variant="outline"
                    onClick={() => {
                      toggleLanguage();
                      setMobileOpen(false);
                    }}
                    className="flex items-center justify-center gap-2"
                    data-testid="mobile-language-toggle"
                  >
                    <Globe className="h-4 w-4" />
                    {language === 'en' ? 'العربية' : 'English'}
                  </Button>

                  {/* Mobile Nav Items */}
                  <nav className="flex flex-col gap-2">
                    {navItems.map((item, index) => (
                      item.children ? (
                        <div key={index} className="flex flex-col gap-1">
                          <span className="px-3 py-2 text-sm font-semibold text-[#6B7280]">
                            {item.label}
                          </span>
                          {item.children.map((child, childIndex) => (
                            <Link
                              key={childIndex}
                              to={child.href}
                              onClick={() => setMobileOpen(false)}
                              className={`px-6 py-2 text-sm transition-colors ${
                                isActive(child.href)
                                  ? 'text-[#33AADD] bg-[#33AADD]/5'
                                  : 'text-[#111827] hover:text-[#33AADD] hover:bg-[#F9FAFB]'
                              }`}
                              data-testid={`mobile-nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <Link
                          key={index}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className={`px-3 py-2 text-sm font-medium transition-colors ${
                            isActive(item.href)
                              ? 'text-[#33AADD] bg-[#33AADD]/5'
                              : 'text-[#111827] hover:text-[#33AADD] hover:bg-[#F9FAFB]'
                          }`}
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {item.label}
                        </Link>
                      )
                    ))}
                  </nav>

                  {/* Mobile Auth */}
                  {user ? (
                    <div className="flex flex-col gap-2 border-t pt-4">
                      <div className="flex items-center gap-3 px-3 py-2">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-[#33AADD] flex items-center justify-center text-white font-medium">
                            {user.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-[#111827]">{user.name}</p>
                          <p className="text-xs text-[#6B7280]">{user.email}</p>
                        </div>
                      </div>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-[#111827] hover:text-[#33AADD]"
                          data-testid="mobile-admin-link"
                        >
                          <LayoutDashboard className="h-4 w-4" />
                          {t('nav.dashboard')}
                        </Link>
                      )}
                      <Button
                        variant="ghost"
                        onClick={() => {
                          logout();
                          setMobileOpen(false);
                        }}
                        className="justify-start"
                        data-testid="mobile-logout-btn"
                      >
                        <LogOut className="me-2 h-4 w-4" />
                        {t('nav.logout')}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 border-t pt-4">
                      <Link to="/login" onClick={() => setMobileOpen(false)}>
                        <Button variant="outline" className="w-full" data-testid="mobile-login-link">
                          {t('nav.login')}
                        </Button>
                      </Link>
                      <Link to="/register" onClick={() => setMobileOpen(false)}>
                        <Button className="w-full btn-primary" data-testid="mobile-register-link">
                          {t('nav.register')}
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
